import { useState } from 'react';
import { GoArrowUpRight } from 'react-icons/go';
import { getAuthUrl } from '@podus/utils';
import { ScrollReveal } from '@podus/ui';

const audiences = [
    {
        number: '01',
        title: 'Individuals & Citizens',
        description:
            'Access clear legal guidance, draft documents, resolve disputes, and connect with lawyers. Podus helps individuals understand their rights and take action without navigating complicated legal systems alone.',
    },
    {
        number: '02',
        title: 'Businesses & Entrepreneurs',
        description:
            'Draft and analyze contracts, track obligations, and conduct employee background checks. Podus helps businesses reduce legal risk, make informed decisions, and manage agreements with greater clarity and confidence.',
    },
    {
        number: '03',
        title: 'Lawyers & Legal Students & Professionals',
        description:
            'Offer consultations, review contracts, support dispute resolution, and participate in legal aid. Podus connects legal professionals with users while creating opportunities to build experience and expand professional impact.',
    },
    {
        number: '04',
        title: 'NGOs & Human Rights Organizations',
        description:
            'Support communities, document rights violations, and connect affected individuals to legal support. Podus provides digital tools that help NGOs strengthen advocacy, manage cases, and scale justice initiatives.',
    },
];

export default function Audience() {
    const [activeIndex, setActiveIndex] = useState(0);

    const toggle = (i: number) => setActiveIndex(activeIndex === i ? -1 : i);

    return (
        <section className="px-4 py-10 lg:px-16 lg:py-24 xl:px-20">
            {/* Section Header */}
            <div className="pb-6 pt-10 2xl:pb-12 2xl:pt-24">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:gap-16">
                    <ScrollReveal variant="fade-up" delay={0}>
                        <div>
                            <h2 className="text-secondary max-w-2xl text-[28px] font-extrabold leading-[1.1] tracking-tight sm:text-[36px] lg:text-[44px] xl:text-[56px] 2xl:text-[64px]">
                                Who Can Use Podus AI
                            </h2>
                        </div>
                    </ScrollReveal>
                    <ScrollReveal variant="fade-up" delay={150}>
                        <div>
                            <p className="text-[#4E4D4D] leading-[2] max-w-3xl text-sm font-medium lg:text-[15px]">
                                Podus AI is designed for everyone — from individuals seeking justice to businesses
                                managing contracts and lawyers looking to streamline their work. Whether you need quick
                                legal guidance, affordable representation, or secure document management, Podus gives
                                you the tools to take control of your legal journey.
                            </p>
                        </div>
                    </ScrollReveal>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-8">
                {/* Accordion */}
                <ScrollReveal variant="fade-right" delay={100} distance={30} className="lg:w-[45%] lg:shrink-0">
                    <div className="bg-[#F5F5F5] rounded-2xl lg:rounded-3xl px-5 lg:px-8 py-2 lg:py-4">
                        {audiences.map((item, i) => {
                            const isOpen = activeIndex === i;
                            return (
                                <div key={i} className={i > 0 ? 'border-t border-gray-200' : ''}>
                                    <button
                                        type="button"
                                        className="flex items-center gap-4 w-full py-6 lg:py-7 text-left cursor-pointer"
                                        onClick={() => toggle(i)}
                                    >
                                        <span className="w-10 h-10 lg:w-11 lg:h-11 rounded-full border-[1.5px] border-secondary flex items-center justify-center text-xs lg:text-sm font-semibold text-secondary shrink-0">
                                            {item.number}
                                        </span>
                                        <span className="flex-1 text-lg lg:text-xl font-extrabold text-secondary leading-snug">
                                            {item.title}
                                        </span>
                                        <span className="relative w-5 h-5 flex items-center justify-center shrink-0">
                                            <span className="absolute w-3.5 h-0.5 bg-secondary rounded-full" />
                                            <span
                                                className={`absolute w-3.5 h-0.5 bg-secondary rounded-full transition-transform duration-300 ${isOpen ? '' : 'rotate-90'}`}
                                            />
                                        </span>
                                    </button>

                                    <div
                                        className="grid"
                                        style={{
                                            gridTemplateRows: isOpen ? '1fr' : '0fr',
                                            transition: 'grid-template-rows 0.3s ease-out',
                                        }}
                                    >
                                        <div className="overflow-hidden">
                                            <p className="text-[#6B7280] text-sm lg:text-[15px] leading-[2] pb-6 lg:pb-7 pl-14 lg:pl-15 pr-8">
                                                {item.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </ScrollReveal>

                {/* Images Grid — Desktop only */}
                <div className="hidden lg:grid grid-cols-2 flex-1 gap-4">
                    <ScrollReveal variant="fade-up" delay={200} distance={30} className="row-span-2">
                        <div className="rounded-2xl overflow-hidden relative h-full">
                            <img
                                src="/tem/team-image.svg"
                                alt="People collaborating"
                                className="w-full h-full object-cover"
                            />
                            <button
                                type="button"
                                className="absolute top-5 right-5 w-12 h-12 bg-primary rounded-full flex items-center justify-center text-white hover:bg-primary-400 hover:scale-105 active:scale-95 transition-all duration-200"
                                aria-label="Explore"
                            >
                                <GoArrowUpRight className="w-5 h-5" />
                            </button>
                        </div>
                    </ScrollReveal>

                    <ScrollReveal variant="fade-up" delay={300} distance={30}>
                        <div className="rounded-2xl overflow-hidden">
                            <img
                                src="/tem/team-image-2.svg"
                                alt="Business professionals meeting"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </ScrollReveal>

                    <ScrollReveal variant="scale" delay={400} duration={800}>
                        <div className="bg-secondary rounded-2xl p-6 xl:p-8 flex flex-col justify-center items-center text-center h-full">
                            <span className="text-white text-5xl lg:text-6xl xl:text-7xl font-bold leading-none">
                                70%
                            </span>
                            <p className="text-white/60 text-sm mt-2 leading-snug">Time Saved On Routine Tasks</p>
                            <a
                                href={`${getAuthUrl()}/sign-up`}
                                className="mt-5 bg-primary text-white rounded-full px-8 py-3 text-sm font-medium hover:bg-primary-400 hover:scale-[1.03] active:scale-[0.98] transition-all duration-200 w-full text-center"
                            >
                                Get Started
                            </a>
                        </div>
                    </ScrollReveal>
                </div>

                {/* Stat Card — Mobile only */}
                <ScrollReveal variant="fade-up" delay={100} className="lg:hidden">
                    <div className="bg-secondary rounded-3xl p-8 pt-10">
                        <span className="text-white text-5xl sm:text-6xl font-bold leading-none block">70%</span>
                        <p className="text-white/60 text-sm mt-3 leading-snug">Time Saved On Routine Tasks</p>
                        <a
                            href={`${getAuthUrl()}/sign-up`}
                            className="mt-6 bg-primary text-white rounded-full px-8 py-3.5 text-sm font-medium hover:bg-primary-400 transition-colors inline-block"
                        >
                            Get Started
                        </a>
                    </div>
                </ScrollReveal>
            </div>
        </section>
    );
}
